import { apiClient } from './apiClient';
import type { FacilityBookingsResponse, CreateReservationRequest, FacilityReservation } from '../types/facilityBookings';
import type { Facility, CreateFacilityRequest, UpdateFacilityRequest, FacilitySchedule, DateSpecificSlots, FacilityAvailability, GridTimeSlot } from '../types/facility';

export const facilityService = {
  /**
   * Get all facilities for the current user's business (legacy - uses userId)
   */
  async getMyFacilities(token: string): Promise<Facility[]> {
    return apiClient.get<Facility[]>('/Facility/my-facilities', token);
  },

  /**
   * Get all facilities for a specific business profile
   */
  async getFacilitiesByBusinessProfile(businessProfileId: string, token: string): Promise<Facility[]> {
    return apiClient.get<Facility[]>(`/business-profile/${businessProfileId}/facilities`, token);
  },

  /**
   * Get a single facility by ID
   */
  async getFacility(facilityId: string, token: string): Promise<Facility> {
    return apiClient.get<Facility>(`/Facility/${facilityId}`, token);
  },

  /**
   * Create a new facility
   */
  async createFacility(data: CreateFacilityRequest, token: string): Promise<Facility> {
    return apiClient.post<Facility>('/Facility', data, token);
  },

  /**
   * Update an existing facility
   */
  async updateFacility(facilityId: string, data: UpdateFacilityRequest, token: string): Promise<Facility> {
    return apiClient.put<Facility>(`/Facility/${facilityId}`, data, token);
  },

  /**
   * Delete a facility
   */
  async deleteFacility(facilityId: string, token: string): Promise<void> {
    return apiClient.delete(`/Facility/${facilityId}`, token);
  },

  /**
   * Get facility time slots (legacy format)
   */
  async getFacilityTimeSlotsLegacy(facilityId: string, date: string | undefined, token: string): Promise<{
    allTimeSlots?: FacilitySchedule;
    dateSpecificSlots?: DateSpecificSlots;
  }> {
    const endpoint = date
      ? `/Facility/${facilityId}/timeslots/${date}`
      : `/Facility/${facilityId}/timeslots`;
    return apiClient.get(endpoint, token);
  },

  /**
   * Get facility time slots for a specific date (grid format)
   */
  async getFacilityTimeSlots(facilityId: string, date: string, token: string): Promise<{
    timeSlots: GridTimeSlot[];
  }> {
    const endpoint = `/Facility/${facilityId}/timeslots/${date}`;
    return apiClient.get(endpoint, token);
  },

  /**
   * Update facility time slots (legacy format)
   */
  async updateFacilityTimeSlotsLegacy(
    facilityId: string,
    timeSlots: {
      allTimeSlots?: FacilitySchedule;
      dateSpecificSlots?: DateSpecificSlots;
    },
    token: string
  ): Promise<void> {
    return apiClient.put(`/Facility/${facilityId}/timeslots`, timeSlots, token);
  },

  /**
   * Update facility availability (grid format)
   */
  async updateFacilityAvailability(
    facilityId: string,
    availability: FacilityAvailability,
    token: string
  ): Promise<void> {
    return apiClient.put(`/Facility/${facilityId}/availability`, availability, token);
  },

  /**
   * Fetches facility bookings data for a specific date
   * Requires authentication token
   * @param facilityId - The ID of the facility
   * @param date - The date in YYYY-MM-DD format
   * @param token - Authentication token
   */
  async getBookingsByDate(
    facilityId: string,
    date: string,
    token: string
  ): Promise<FacilityBookingsResponse> {
    return apiClient.get<FacilityBookingsResponse>(
      `/Facility/${facilityId}/bookings/${date}`,
      token
    );
  },

  /**
   * Creates a new reservation
   * Requires authentication token
   * @param data - Reservation data
   * @param token - Authentication token
   */
  async createReservation(
    data: CreateReservationRequest,
    token: string
  ): Promise<FacilityReservation> {
    return apiClient.post<FacilityReservation>(
      '/Reservation/admin',
      data,
      token
    );
  },
};
